import React from 'react';
import { FunnelIcon, PlusIcon, MinusIcon } from '@heroicons/react/24/outline';
import { FormSelect } from './FormSelect';
import { FormInput } from './FormInput';
import { Category, Destination, Person } from '../types/budget';
import { useTranslation } from 'react-i18next';

interface TransactionFiltersState {
  category: Category | '';
  responsible: Person | '';
  destination: Destination | '';
  amountType: '' | 'income' | 'expense';
  search: string;
}

interface TransactionFiltersProps {
  filters: TransactionFiltersState;
  setFilters: React.Dispatch<React.SetStateAction<TransactionFiltersState>>;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  transactionCount: number;
  showAddButton?: boolean;
  onAddClick?: (type: 'income' | 'expense') => void;
  categories: { id: string; name: string }[]; // Use the categories from the budget
  responsibles: { id: string; name: string }[]; // Use the responsibles from the budget
  destinations: { id: string; name: string }[]; // Use the destinations from the budget
}

export const TransactionFilters: React.FC<TransactionFiltersProps> = ({
  filters,
  setFilters,
  showFilters,
  setShowFilters,
  transactionCount,
  showAddButton = true,
  onAddClick,
  categories = [],
  responsibles = [],
  destinations = [],
}) => {
  const { t, i18n } = useTranslation();

  // Convert categories, responsibles, and destinations to the format expected by FormSelect
  const categoryOptions = categories
    .map((category) => ({
      value: category.name,
      label: category.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const responsibleOptions = responsibles
    .map((responsible) => {
      // Check if the responsible name matches any translation of "common"
      const languages = i18n.languages || [];
      const isCommon = languages.some(
        (lang) => i18n.getFixedT(lang)('defaults.responsibles.common') === responsible.name
      );

      return {
        value: isCommon ? 'common' : responsible.name,
        label: responsible.name,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  const destinationOptions = destinations
    .map((destination) => ({
      value: destination.name,
      label: destination.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <div className="rounded-lg bg-white px-6 pt-6 dark:bg-gray-800">
      <div className="mb-6 flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div className="flex flex-wrap items-center gap-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors duration-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
          >
            <FunnelIcon className="mr-2 h-5 w-5" />
            {t('transaction.filters.title')}
          </button>
          <div className="rounded-lg bg-gray-50 px-4 py-2 text-sm font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-400">
            {t('transaction.count', { count: transactionCount })}
          </div>
        </div>
        {showAddButton && onAddClick && (
          <div className="flex space-x-3">
            <button
              onClick={() => onAddClick('income')}
              data-attr="add-income-button"
              className="flex items-center rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 dark:bg-emerald-500 dark:hover:bg-emerald-600"
            >
              <PlusIcon className="mr-2 h-5 w-5" />
              {t('transaction.newIncome')}
            </button>
            <button
              onClick={() => onAddClick('expense')}
              data-attr="add-expense-button"
              className="flex items-center rounded-md bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-offset-2 dark:bg-rose-500 dark:hover:bg-rose-600"
            >
              <MinusIcon className="mr-2 h-5 w-5" />
              {t('transaction.newExpense')}
            </button>
          </div>
        )}
      </div>

      {showFilters && (
        <div className="mb-6 space-y-4 rounded-lg bg-gray-50 p-6 dark:bg-gray-900">
          <FormInput
            label={t('transaction.filters.search')}
            value={filters.search}
            onChange={(value) => setFilters((f) => ({ ...f, search: value as string }))}
            placeholder={t('transaction.filters.searchPlaceholder')}
            hideLabel
          />
          <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
            <FormSelect
              label={t('transaction.category')}
              value={filters.category}
              onChange={(value) => setFilters((f) => ({ ...f, category: value as Category }))}
              options={categoryOptions}
              placeholder={t('transaction.filters.allCategories')}
              hideLabel
            />

            <FormSelect
              label={t('transaction.responsible')}
              value={filters.responsible}
              onChange={(value) => setFilters((f) => ({ ...f, responsible: value as Person }))}
              options={responsibleOptions}
              placeholder={t('transaction.filters.allResponsibles')}
              hideLabel
            />

            <FormSelect
              label={t('transaction.destination')}
              value={filters.destination}
              onChange={(value) => setFilters((f) => ({ ...f, destination: value as Destination }))}
              options={destinationOptions}
              placeholder={t('transaction.filters.allDestinations')}
              hideLabel
            />

            <FormSelect
              label={t('transaction.amount')}
              value={filters.amountType}
              onChange={(value) =>
                setFilters((f) => ({ ...f, amountType: value as '' | 'income' | 'expense' }))
              }
              options={[
                { value: 'income', label: t('transaction.filters.incomesOnly') },
                { value: 'expense', label: t('transaction.filters.expensesOnly') },
              ].sort((a, b) => a.label.localeCompare(b.label))}
              placeholder={t('transaction.filters.allAmounts')}
              hideLabel
            />
          </div>

          <div className="flex justify-end pt-4">
            <button
              onClick={() =>
                setFilters({
                  category: '',
                  responsible: '',
                  destination: '',
                  amountType: '',
                  search: '',
                })
              }
              className="text-sm font-medium text-gray-600 transition-colors duration-200 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
            >
              {t('transaction.filters.reset')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
